import React from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import {
  ViewColumn,
  Code,
  Visibility,
  Save,
  FileDownload,
  FileUpload,
  Sync,
  SmartToy,
} from '@mui/icons-material';

interface ViewTitleBarProps {
  viewName: string;
  mode: 'grid' | 'json' | 'preview';
  onModeChange: (mode: 'grid' | 'json' | 'preview') => void;
  onSave: () => void;
  onExport: () => void;
  onDownloadExcel: () => void;
  onUploadExcel: () => void;
  onSyncOCI: () => void;
  onToggleChat: () => void;
  isDirty: boolean;
  ociAvailable: boolean;
  ociSyncing: boolean;
  uploadingExcel: boolean;
}

const modeOptions = [
  { value: 'grid' as const, label: 'Grid Editor', icon: <ViewColumn sx={{ fontSize: 14 }} /> },
  { value: 'json' as const, label: 'JSON', icon: <Code sx={{ fontSize: 14 }} /> },
  { value: 'preview' as const, label: 'Preview', icon: <Visibility sx={{ fontSize: 14 }} /> },
];

const ViewTitleBar: React.FC<ViewTitleBarProps> = ({
  viewName,
  mode,
  onModeChange,
  onSave,
  onExport,
  onDownloadExcel,
  onUploadExcel,
  onSyncOCI,
  onToggleChat,
  isDirty,
  ociAvailable,
  ociSyncing,
  uploadingExcel,
}) => {
  const outlinedButtonSx = {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    padding: '5px 10px',
    borderRadius: '5px',
    fontSize: 12,
    fontWeight: 500,
    border: '1px solid #DDD',
    background: 'transparent',
    color: '#666',
    cursor: 'pointer',
    '&:hover': { background: '#F5F5F5' },
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '10px 16px',
        borderBottom: '1px solid #EEE',
        background: 'white',
      }}
    >
      {/* Left: View name */}
      <Typography
        component="h2"
        sx={{ margin: 0, fontSize: 18, fontWeight: 600, color: '#212121' }}
      >
        {viewName}
      </Typography>

      {/* Center: Mode toggle group */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          background: '#F0F0F0',
          borderRadius: '6px',
          padding: '2px',
        }}
      >
        {modeOptions.map((opt) => {
          const isActive = mode === opt.value;
          return (
            <Box
              key={opt.value}
              component="button"
              onClick={() => onModeChange(opt.value)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                padding: '4px 10px',
                borderRadius: '4px',
                fontSize: 12,
                border: 'none',
                cursor: 'pointer',
                fontWeight: isActive ? 600 : 400,
                color: isActive ? '#333' : '#888',
                background: isActive ? 'white' : 'transparent',
                boxShadow: isActive ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                transition: 'all 0.15s ease',
              }}
            >
              {opt.icon}
              {opt.label}
            </Box>
          );
        })}
      </Box>

      {/* Right: Action buttons */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          borderLeft: '1px solid #E0E0E0',
          paddingLeft: '12px',
        }}
      >
        {/* Save */}
        <Box
          component="button"
          onClick={isDirty ? onSave : undefined}
          disabled={!isDirty}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            padding: '5px 10px',
            borderRadius: '5px',
            fontSize: 12,
            fontWeight: 500,
            border: 'none',
            background: isDirty ? '#00897B' : '#BDBDBD',
            color: 'white',
            cursor: isDirty ? 'pointer' : 'not-allowed',
            '&:hover': { background: isDirty ? '#00796B' : '#BDBDBD' },
          }}
        >
          <Save sx={{ fontSize: 14 }} />
          Save
        </Box>

        {/* Export */}
        <Box component="button" onClick={onExport} sx={outlinedButtonSx}>
          <FileDownload sx={{ fontSize: 14 }} />
          Export
        </Box>

        {/* Download Excel */}
        <Box component="button" onClick={onDownloadExcel} sx={outlinedButtonSx}>
          <FileDownload sx={{ fontSize: 14 }} />
          Excel
        </Box>

        {/* Upload Excel */}
        <Box
          component="button"
          onClick={onUploadExcel}
          disabled={uploadingExcel}
          sx={{
            ...outlinedButtonSx,
            opacity: uploadingExcel ? 0.6 : 1,
            cursor: uploadingExcel ? 'not-allowed' : 'pointer',
          }}
        >
          {uploadingExcel ? (
            <CircularProgress size={14} sx={{ color: '#666' }} />
          ) : (
            <FileUpload sx={{ fontSize: 14 }} />
          )}
          Upload
        </Box>

        {/* Sync OCI */}
        {ociAvailable && (
          <Box
            component="button"
            onClick={onSyncOCI}
            disabled={ociSyncing}
            sx={{
              ...outlinedButtonSx,
              opacity: ociSyncing ? 0.6 : 1,
              cursor: ociSyncing ? 'not-allowed' : 'pointer',
            }}
          >
            {ociSyncing ? (
              <CircularProgress size={14} sx={{ color: '#666' }} />
            ) : (
              <Sync sx={{ fontSize: 14 }} />
            )}
            Sync OCI
          </Box>
        )}

        {/* AI Toggle */}
        <Box
          component="button"
          onClick={onToggleChat}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            padding: '5px 10px',
            borderRadius: '5px',
            fontSize: 12,
            fontWeight: 500,
            border: 'none',
            background: '#7C4DFF',
            color: 'white',
            cursor: 'pointer',
            '&:hover': { background: '#651FFF' },
          }}
        >
          <SmartToy sx={{ fontSize: 14 }} />
          AI
        </Box>
      </Box>
    </Box>
  );
};

export default ViewTitleBar;
